import * as React from "react";
import { Bell } from "lucide-react";
import moment from "../../../utilities/Moment";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { AnyAction, Dispatch } from "redux";
import { DashboardController } from "../../../controllers";
import ERoute from "../../../ERoute";
import RouteHelper from "../../../helpers/RouteHelper";
import { showSendLedgerItemModal } from "../../../store/modals/actions";
import { DashboardOverdueInvoice, DashboardOverdueInvoices, LedgerItemStatus } from "../../../types";
import NumberFormatter from "../../../utilities/NumberFormatter";
import CardEmptyInfo from "../../Card/CardEmptyInfo";
import PageLoader from "../../Page/PageLoader";
import Panel from "../../Panel/Panel";
import { Button, buttonVariants } from "../../ui/button";

interface IProps {
  currency: string;
  numberFormat: string;
}

const OverdueInvoicesWidget = (props: IProps) => {
  const { numberFormat } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch() as Dispatch<AnyAction>;
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState<DashboardOverdueInvoices | null>(null);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setData(await DashboardController.getOverdueInvoices());
    } catch (error) {
      console.error("Failed to fetch overdue invoices:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onRemindClick = (invoice: DashboardOverdueInvoice) => {
    dispatch(
      showSendLedgerItemModal({
        ledgerItemId: invoice.id,
        reminder: true,
        onSubmit: () => fetchData(),
      })
    );
  };

  const title = t("OverdueInvoicesWidget::Overdue invoices");
  const viewAllLink = (
    <Link
      to={RouteHelper.process(ERoute.PATH_INVOICES, { status: LedgerItemStatus.OVERDUE })}
      className={buttonVariants({ variant: "link", size: "sm" })}
    >
      {t("OverdueInvoicesWidget::View all overdue")}
    </Link>
  );

  if (isLoading || !data) {
    return (
      <Panel className="h-full flex flex-col mb-0!" contentClasses="min-h-0 flex-1 overflow-auto" title={title}>
        {isLoading ? <PageLoader /> : null}
      </Panel>
    );
  }

  return (
    <Panel
      className="h-full flex flex-col mb-0!"
      contentClasses="min-h-0 flex-1 flex flex-col p-0!"
      title={title}
      headerRight={data.count > 0 ? viewAllLink : undefined}
    >
      {data.count === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <CardEmptyInfo icon="invoice" description={t("OverdueInvoicesWidget::No overdue invoices")} />
        </div>
      ) : (
        <>
          <div className="flex shrink-0 gap-8 border-b px-4 py-3">
            <div>
              <div className="text-xs text-muted-foreground">{t("OverdueInvoicesWidget::Total overdue")}</div>
              <div className="text-xl font-bold text-destructive tabular-nums">
                {NumberFormatter.formatCurrency(data.currency, numberFormat, Number(data.total))}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{t("OverdueInvoicesWidget::Invoices")}</div>
              <div className="text-xl font-bold tabular-nums">{data.count}</div>
            </div>
          </div>

          {/* On phones widgets take their natural height, so cap the list there and let it scroll */}
          <ul className="m-0 min-h-0 flex-1 list-none overflow-auto p-0 in-[.dashboard-mobile]:max-h-80">
            {data.invoices.map((invoice) => (
              <li key={invoice.id} className="flex items-center gap-3 border-b px-4 py-2.5 last:border-b-0">
                <Link
                  to={RouteHelper.process(ERoute.PATH_INVOICE, { id: invoice.id })}
                  className="min-w-0 flex-1 text-foreground no-underline hover:text-foreground"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate font-medium">{invoice.contact_name || "-"}</span>
                    <span className="shrink-0 translate-y-2 font-semibold tabular-nums">
                      {NumberFormatter.formatCurrency(invoice.currency, numberFormat, Number(invoice.open_amount))}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-1.5 text-xs text-muted-foreground">
                    <span>{invoice.number}</span>
                    <span>·</span>
                    <span className="text-destructive">
                      {invoice.days_overdue > 0
                        ? t("OverdueInvoicesWidget::{{count}} days overdue", { count: invoice.days_overdue })
                        : t("OverdueInvoicesWidget::Due today")}
                    </span>
                    <span>·</span>
                    {invoice.reminders_count > 0 ? (
                      <span>
                        {t("OverdueInvoicesWidget::Reminded {{count}}×", { count: invoice.reminders_count })}
                        {invoice.last_reminder_sent_at && ` · ${moment(invoice.last_reminder_sent_at).fromNow()}`}
                      </span>
                    ) : (
                      <span>{t("OverdueInvoicesWidget::Not reminded yet")}</span>
                    )}
                  </div>
                </Link>
                <Button variant="outline" size="sm" onClick={() => onRemindClick(invoice)}>
                  <Bell data-icon="inline-start" />
                  {t("OverdueInvoicesWidget::Remind")}
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Panel>
  );
};

export default OverdueInvoicesWidget;
